import { Dices } from 'lucide-react';
import { bettingOf, type BettingContent, type Team } from '@london-life/contracts';
import { Button } from '@london-life/ui/components/button';
import { Input } from '@london-life/ui/components/input';
import { Switch } from '@london-life/ui/components/switch';
import { Textarea } from '@london-life/ui/components/textarea';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { useAdmin } from '../../lib/admin-context';
import { useContent } from './use-content';
import { ContentBar } from './versions';
import { Field, NumberField, idFor, type NumberSpec } from './form';

type BetNumbers = Exclude<keyof BettingContent, 'visible' | 'name' | 'notice' | 'help' | 'teams'>;
const STAKES: NumberSpec<BetNumbers>[] = [
  { key: 'minStakePence', label: 'Smallest bet', unit: 'pounds', min: 1, max: 1_000_000_000 },
  { key: 'maxStakePence', label: 'Largest bet', unit: 'pounds', min: 1, max: 1_000_000_000 },
  { key: 'dailyStakePence', label: 'Most staked in a London day', unit: 'pounds', min: 1, max: 10_000_000_000 },
  { key: 'realityEvery', label: 'Reality check every (bets)', unit: 'number', min: 5, max: 500, whole: true },
];
const GAMES: NumberSpec<BetNumbers>[] = [
  { key: 'margin', label: "The house's margin", unit: 'percent', min: 0, max: 0.3 },
  { key: 'gamesPerDay', label: 'Football games a day', unit: 'number', min: 1, max: 20, whole: true },
  { key: 'resultsHour', label: 'Results come in at (hour, London)', unit: 'number', min: 0, max: 23, whole: true },
];
const STRENGTH: NumberSpec<'strength'> = {
  key: 'strength',
  label: 'Strength (1 to 100)',
  unit: 'number',
  min: 1,
  max: 100,
  whole: true,
};

// Bets (as Lagos Life's LagosBet), built to the rules: the notice every screen shows and the help line, the
// stakes and the day's limit, the reality check, the margin, and the fictional clubs. Part of the content
// draft, published with it.
export function BetsPage() {
  const { can } = useAdmin();
  const editor = useContent();
  const working = editor.working;
  const readOnly = !can('content.write');
  const betting = bettingOf(working ?? {});
  const change = (fn: (draft: BettingContent) => void) =>
    editor.edit((content) => {
      content.betting ??= structuredClone(bettingOf(content));
      fn(content.betting);
    });
  const team = (id: string, fn: (entry: Team) => void) =>
    change((draft) => {
      const entry = draft.teams.find((item) => item.id === id);
      if (entry) fn(entry);
    });
  const numbers = (specs: NumberSpec<BetNumbers>[]) =>
    specs.map((spec) => (
      <NumberField
        key={spec.key}
        id={`bets-${spec.key}`}
        spec={spec}
        value={betting[spec.key]}
        onChange={(value) => change((draft) => void (draft[spec.key] = value))}
      />
    ));
  return (
    <section>
      <PageHeader
        title="Bets"
        description="The betting app (as Lagos Life's LagosBet), to the rules: game money only, 18+, limits, breaks and a reality check. Changes are a draft until they are published."
      />
      <ContentBar editor={editor} />
      {editor.failure && <ErrorNotice error={editor.failure} onRetry={() => void editor.reload()} />}
      {!working && !editor.failure && <p className="admin-muted">Loading…</p>}
      {working && (
        <div className="content-editor">
          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="The app">
            <h2 className="content-subtitle">
              <Dices size={16} aria-hidden="true" /> The app
            </h2>
            <label className="designer-check">
              <Switch
                checked={betting.visible}
                aria-label="Bets open"
                onCheckedChange={(on) => change((draft) => void (draft.visible = on))}
              />{' '}
              Players can bet
            </label>
            <div className="content-grid">
              <Field id="bets-name" label="Name">
                <Input
                  id="bets-name"
                  value={betting.name}
                  maxLength={30}
                  onChange={(e) => change((draft) => void (draft.name = e.target.value))}
                />
              </Field>
            </div>
            <Field id="bets-notice" label="Notice on every screen">
              <Textarea
                id="bets-notice"
                value={betting.notice}
                maxLength={200}
                onChange={(e) => change((draft) => void (draft.notice = e.target.value))}
              />
            </Field>
            <Field id="bets-help" label="Where to get help (safer play)">
              <Textarea
                id="bets-help"
                value={betting.help}
                maxLength={300}
                onChange={(e) => change((draft) => void (draft.help = e.target.value))}
              />
            </Field>
          </fieldset>
          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Stakes and limits">
            <h2 className="content-subtitle">Stakes and limits</h2>
            <p className="admin-muted">
              Players can set a lower daily limit of their own; a raise waits 24 hours. A reality check shows after this
              many bets in a session.
            </p>
            <div className="content-grid">{numbers(STAKES)}</div>
          </fieldset>
          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Games">
            <h2 className="content-subtitle">Games</h2>
            <div className="content-grid">{numbers(GAMES)}</div>
          </fieldset>
          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Clubs">
            <h2 className="content-subtitle">Clubs</h2>
            <p className="admin-muted">
              Fictional London clubs only (real clubs&apos; names are their trademarks). The day&apos;s games are drawn
              from these; stronger clubs win more often and get shorter odds.
            </p>
            {betting.teams.map((item) => (
              <div className="content-grid content-row" key={item.id} data-testid={`club-${item.id}`}>
                <Field id={`club-${item.id}-name`} label="Name">
                  <Input
                    id={`club-${item.id}-name`}
                    value={item.name}
                    maxLength={40}
                    onChange={(e) => team(item.id, (entry) => void (entry.name = e.target.value))}
                  />
                </Field>
                <NumberField
                  id={`club-${item.id}-strength`}
                  spec={STRENGTH}
                  value={item.strength}
                  onChange={(value) => team(item.id, (entry) => void (entry.strength = value))}
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={betting.teams.length <= betting.gamesPerDay * 2}
                  onClick={() =>
                    change((draft) => void (draft.teams = draft.teams.filter((entry) => entry.id !== item.id)))
                  }
                >
                  Remove {item.name}
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                change((draft) =>
                  draft.teams.push({
                    id: idFor('New club', new Set(draft.teams.map((entry) => entry.id)), 'club', 30),
                    name: 'New club',
                    strength: 60,
                  }),
                )
              }
            >
              Add a club
            </Button>
          </fieldset>
        </div>
      )}
    </section>
  );
}
